import {useCallback, useEffect, useMemo, useRef} from 'react'
import {getState, updateForm, useAutoForm, useSelector} from '../../../store.js'
import {
  useZropdown,
  useZropdownElement,
} from '../../../common/components/Zropdown.js'
import {currentDropdownSelector} from '../../../redux/selectors/ui/index.js'
import FilterDropdown from '../../../common/components/FilterDropdown.js'
import {
  boxShapesByShipperTypeSelector,
  getBoxShapeOptions,
  isLabelPropertyEnabledSelector,
  labelConfigSelector,
  labelTypeSelector,
  onLabelPropertyValueChange,
  orderTypeSelector,
  shipperTypesSelector,
  updateLabelConfig,
} from '../../../data/labelInfos/index.js'
import {
  filterOptions,
  getNounsFromValue,
  tokenizeOptions,
} from '../../../common/tokenizeOptions.js'
import className from '../../../common/className.js'
import Checkbox from '../../../common/components/Checkbox.js'
import useLabelProperty, {
  SHIPPER_DEPENDENT_PROPERTIES_FUNC,
  formatMetaClassName,
} from './useLabelProperty.js'
import EnableLabelProperty, {PickLabelProperty} from './EnableLabelProperty.js'
import {
  Count,
  IfPluralNotZero,
  IfSingle,
  IfZero,
  Plural,
  PluralBlock,
} from '../../../common/components/Plural.js'
import {
  SHIPPER_NAMES,
  SHIPPER_VENDORS,
} from '../../../common/constants/ShipperNames.js'
import useStable from '../../../common/useStable.js'
import {useLabelConfigContext} from '../LabelConfigContext.js'
import OverrideLabelProperty from './OverrideLabelProperty.js'

const PACKAGE_TYPES = 'PACKAGE_TYPES'
const PACKAGE_TYPES__SHIPPER_HEADER = 'PACKAGE_TYPES__SHIPPER_HEADER'
export const LABEL_PROPERTY_PACKAGE_TYPES = 'box_shapes'

export function formatPackageTypesProperty(shipperType) {
  return `${shipperType}__box_shapes`
}

export function packageTypesLabelPropertiesFunc(shipperType, labelInfoID) {
  const shipperTypes = shipperTypesSelector(getState(), {labelInfoID})
  const labelConfig = labelConfigSelector(getState(), {labelInfoID})

  return [
    LABEL_PROPERTY_PACKAGE_TYPES,
    ...shipperTypes
      .map(formatPackageTypesProperty)
      .filter((labelProperty) => (labelConfig[labelProperty] || []).length > 0),
  ]
}

export const VALID_PACKAGE_TYPES_LABEL_PROPERTIES = [
  LABEL_PROPERTY_PACKAGE_TYPES,
  ...SHIPPER_VENDORS.map(formatPackageTypesProperty),
]

function getFirstPackageTypeName(boxShapesByShipperType, boxShapeOptions) {
  for (const [shipperType, boxShapes] of Object.entries(
    boxShapesByShipperType,
  )) {
    if (boxShapes.length === 0) {
      continue
    }

    const option = boxShapeOptions.find(
      (option) =>
        option.shipperType === shipperType && option.value === boxShapes[0],
    )

    return option ? option.display : boxShapes[0]
  }
}

function filterPackageTypes(
  dropdown,
  shipperOptions,
  shipperOptionTokens,
  text,
) {
  try {
    if (!text) {
      updateForm(dropdown, {
        results: shipperOptions,
        hasFoundNothing: false,
      })

      return
    }

    const results = filterOptions(text, shipperOptionTokens)

    updateForm(dropdown, {
      results,
      selectedIndex: 0,
      hasFoundNothing: results.length === 0,
    })
  } catch (err) {
    updateForm(dropdown, {
      serverError: err.message,
    })
  }
}

function onPackageTypeSelected(labelInfoID, onChange, option) {
  const shipperType = option.shipperType
  const labelProperty = formatPackageTypesProperty(shipperType)
  const boxShapesByShipperType = boxShapesByShipperTypeSelector(getState(), {
    labelInfoID,
  })
  const box_shapes = boxShapesByShipperType[shipperType]

  const boxShapes = box_shapes.filter((value) => option.value !== value)

  if (boxShapes.length === box_shapes.length) {
    boxShapes.push(option.value)
  }

  updateLabelConfig(labelInfoID, {
    [labelProperty]: boxShapes,
  })

  onLabelPropertyValueChange(labelInfoID, labelProperty)

  if (onChange) {
    onChange()
  }
}

function toggleAllForShipper(labelInfoID, onChange, option, boxShapeOptions) {
  const shipperType = option.value
  const labelProperty = formatPackageTypesProperty(shipperType)
  const boxShapesByShipperType = boxShapesByShipperTypeSelector(getState(), {
    labelInfoID,
  })
  const box_shapes = boxShapesByShipperType[shipperType]
  const options = boxShapeOptions.filter((o) => o.shipperType === shipperType)

  const boxShapes =
    box_shapes.length === options.length ? [] : options.map(({value}) => value)

  updateLabelConfig(labelInfoID, {
    [labelProperty]: boxShapes,
  })

  onLabelPropertyValueChange(labelInfoID, labelProperty)

  if (onChange) {
    onChange()
  }
}

function Item({option, selected, disabled, isFocused, onSelect}) {
  const id = `${option.shipperType}__box_shape__${option.value}`
  const ref = useRef()

  useZropdownElement({ref, isSelected: isFocused})

  return (
    <li ref={ref} className="list__item list__item--dropdown">
      <span
        className={className`label--selectable label--selectable-pt-pb-0 flex--justify-nowrap label--selectable-left-indent margin-bottom-0 ${{
          'list__link--dropdown-arrow-focus': isFocused,
          disabled,
        }}`}
      >
        <label
          className="flex--justify-nowrap flex-grow flex--jc-left mw-unset margin-bottom-0"
          htmlFor={id}
          data-dropdown-prevent-close
        >
          <span className="margin-right-5">
            <Checkbox
              mode="fancy"
              className="fs-00 v-align-middle"
              id={id}
              checked={selected}
              onClick={() => {
                if (!disabled) {
                  onSelect(option)
                }
              }}
            />
          </span>
          <span className="label--selectable__text">{option.display}</span>
        </label>
      </span>
    </li>
  )
}

function ItemSeparator({option, selected, indeterminate, isFocused, onSelect}) {
  const ref = useRef()

  useZropdownElement({ref, isSelected: isFocused})

  return (
    <li
      ref={ref}
      className="list__item list__item--dropdown list__item--dropdown-header list__item--dropdown-header-pkg-type"
    >
      <span
        className={className`label--selectable label--selectable-pt-pb-0 flex--justify-nowrap margin-bottom-0 ${{
          'list__link--dropdown-arrow-focus': isFocused,
        }}`}
      >
        <label
          className="flex--justify-nowrap flex-grow flex--jc-left mw-unset align-items-center  margin-bottom-0"
          htmlFor={option.value}
          data-dropdown-prevent-close
        >
          <span>
            <Checkbox
              mode="fancy"
              className="fs-00 v-align-middle"
              id={option.value}
              checked={selected}
              indeterminate={indeterminate}
              onClick={() => onSelect(option)}
            />
          </span>
          <span
            className={className`label--selectable__text label__carrier-logo lh-sm ${option.value}`}
          >
            Package Types
          </span>
        </label>
      </span>
    </li>
  )
}

/**
 * Package filter for label info conf
 *
 * @param {Object} props
 * @param {Boolean} props.autoFocus
 * @returns {React.ReactNode}
 */
export default function PackageTypesFilter({autoFocus}) {
  const {contextID, labelInfoID, onChange, isSummary, isPickMode} =
    useLabelConfigContext()
  const propertySpecs = PackageTypesFilter.propertySpecList[0]
  const [labelProperty] = propertySpecs.labelPropertiesFunc(null, labelInfoID)
  const dropdown = `PACKAGE_TYPES_FILTER_${contextID}_${labelInfoID}`

  const form = useAutoForm(dropdown, {
    text: '',
    results: [],
    selectedIndex: 0,
    hasFoundNothing: false,
    serverError: null,
    hasChanged: false,
  })
  const localOnChange = useCallback(() => {
    updateForm(dropdown, {hasChanged: true})
  }, [])

  const config = useSelector((state) =>
    labelConfigSelector(state, {labelInfoID}),
  )
  const shipperTypes = useStable(
    useSelector((state) => shipperTypesSelector(state, {labelInfoID})),
  )
  const orderType = useSelector((state) =>
    orderTypeSelector(state, {labelInfoID}),
  )
  const labelType = useSelector((state) =>
    labelTypeSelector(state, {labelInfoID}),
  )
  const isLabelPropertyEnabled = useSelector((state) =>
    isLabelPropertyEnabledSelector(state, {
      labelInfoID,
      labelProperty,
    }),
  )

  const boxShapeOptions = useStable(
    useMemo(
      () =>
        shipperTypes.reduce((prev, shipperType) => {
          const boxShapes = getBoxShapeOptions(
            config,
            shipperType,
            orderType,
            labelType,
          )

          if (!boxShapes) {
            return prev
          }

          const options = []
          for (const option of boxShapes) {
            options.push({
              value: option.value,
              display: option.display,
              entity: option,
              shipperType,
              type: PACKAGE_TYPES,
              nouns: [...getNounsFromValue(option.display), shipperType],
            })
          }

          prev.push({
            value: shipperType,
            display: SHIPPER_NAMES[shipperType],
            entity: options.map(({entity}) => entity),
            type: PACKAGE_TYPES__SHIPPER_HEADER,
            nouns: new Set(
              options.reduce((prev, {nouns}) => {
                prev.push(...nouns)
                return prev
              }, []),
            ),
          })

          prev.push(...options)

          return prev
        }, []),
      [config, shipperTypes, orderType, labelType],
    ),
  )
  const boxShapeOptionTokens = useMemo(
    () => tokenizeOptions(boxShapeOptions),
    [boxShapeOptions],
  )

  const boxShapesByShipperType = useSelector((state) =>
    boxShapesByShipperTypeSelector(state, {labelInfoID}),
  )
  const boxShapeCount = useMemo(
    () =>
      Object.values(boxShapesByShipperType).reduce(
        (prev, boxShapes) => prev + boxShapes.length,
        0,
      ),
    [boxShapesByShipperType],
  )
  const firstPackageTypeName = getFirstPackageTypeName(
    boxShapesByShipperType,
    boxShapeOptions,
  )

  useEffect(() => {
    filterPackageTypes(
      dropdown,
      boxShapeOptions,
      boxShapeOptionTokens,
      form && form.text,
    )
  }, [boxShapeOptions, form && form.text])

  const buttonRef = useRef()

  const {onChangeInternal, onSelectInternal, onConfirm, onUp, onDown} =
    useZropdown({
      dropdown,
      form,
      onSelect: (option) => {
        if (option.type === PACKAGE_TYPES) {
          onPackageTypeSelected(labelInfoID, localOnChange, option)
        } else {
          toggleAllForShipper(
            labelInfoID,
            localOnChange,
            option,
            boxShapeOptions,
          )
        }
      },
      autoFocus,
    })

  const currentDropdown = useSelector(currentDropdownSelector)

  useEffect(() => {
    if (!currentDropdown && form.hasChanged) {
      updateForm(dropdown, {hasChanged: false})

      if (onChange) {
        onChange()
      }
    }
  }, [currentDropdown])

  useLabelProperty({
    labelProperty,
    propertySpecs,
  })

  const label = 'Package Type'

  if (isSummary) {
    return label
  }

  if (isPickMode && !isLabelPropertyEnabled) {
    return <PickLabelProperty label={label} labelProperty={labelProperty} />
  }

  return (
    <li
      className={className`list__item--shipping-options list__item--shipping-options-rs flex-grow ${formatMetaClassName(
        labelProperty,
      )}`}
    >
      <div className="wrap--edit-preset-form-input">
        <label className="label--bold" htmlFor={dropdown}>
          <span>{label}</span>
          <span className="required">*</span>
        </label>
        <div
          className={`wrap-outer--tag-dropdown-with-filter ${
            currentDropdown === dropdown ? 'open' : ''
          }`}
          data-dropdown={dropdown}
        >
          <button
            id={dropdown}
            ref={buttonRef}
            className="btn btn--dropdown btn--filter btn--filter-w-border w-100"
            type="button"
            disabled={boxShapeOptions.length === 0}
          >
            <span className="btn__text flex">
              <span className="btn__text--no-overflow txt-overflow-ellipsis">
                <span className="txt-overflow-ellipsis">
                  <span>
                    <PluralBlock count={boxShapeCount}>
                      <IfZero>
                        <em>Select a package type...</em>
                      </IfZero>
                      <IfSingle>{firstPackageTypeName}</IfSingle>
                      <IfPluralNotZero>
                        <Count /> <Plural word="package type" />
                      </IfPluralNotZero>
                    </PluralBlock>
                  </span>
                </span>
              </span>
            </span>
          </button>
          <FilterDropdown
            dropdown={dropdown}
            form={form}
            onChangeInternal={onChangeInternal}
            onConfirm={onConfirm}
            onUp={onUp}
            onDown={onDown}
            placeholder="Search package types"
            includeSearchInput
            parentRef={buttonRef}
          >
            {form.results.map((option, index) => {
              const selectedCount = (boxShapesByShipperType[option.value] || [])
                .length

              return option.type === PACKAGE_TYPES ? (
                <Item
                  key={`${option.shipperType}__${option.value}`}
                  option={option}
                  selected={(
                    boxShapesByShipperType[option.shipperType] || []
                  ).includes(option.value)}
                  isFocused={index === form.selectedIndex}
                  onSelect={onSelectInternal}
                />
              ) : (
                <ItemSeparator
                  key={option.value}
                  option={option}
                  selected={selectedCount > 0}
                  indeterminate={
                    option.entity.length !== selectedCount &&
                    selectedCount !== 0
                  }
                  isFocused={index === form.selectedIndex}
                  onSelect={onSelectInternal}
                />
              )
            })}
            {form.hasFoundNothing && (
              <li className="list__item--dropdown list__item--dropdown-empty align-center">
                No package type matches your search.
              </li>
            )}
          </FilterDropdown>
        </div>
      </div>
      <EnableLabelProperty labelProperty={labelProperty} />
      <OverrideLabelProperty
        propertySpecs={propertySpecs}
        label="Override other selected package types when preset is applied"
        helpLink="https://support.ordoro.com/how-do-additive-vs-overwrite-presets-work-in-rate-shopping/"
        helpTitle="Learn More"
      />
    </li>
  )
}

PackageTypesFilter.propertySpecList = [
  {
    validShipperTypes: SHIPPER_VENDORS,
    labelPropertiesFunc: packageTypesLabelPropertiesFunc,
    dependentPropertiesFunc: SHIPPER_DEPENDENT_PROPERTIES_FUNC,
    validLabelPropertiesFunc: () => VALID_PACKAGE_TYPES_LABEL_PROPERTIES,
  },
]
